import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../helpers/useAuth';
import { User } from '../helpers/User';
import { AuthErrorPage } from './AuthErrorPage';
import { ShieldOff } from 'lucide-react';
import { AuthLoadingState } from './AuthLoadingState';
import styles from './ProtectedRoute.module.css';

// Do not use this in pageLayout
const MakeProtectedRoute: (roles: User['role'][]) => React.FC<{
  children: React.ReactNode;
}> = (roles) => {
  const Comp: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { authState } = useAuth();
    const location = useLocation();

    // Show loading state while checking authentication
    if (authState.type === 'loading') {
      return <AuthLoadingState title="Authenticating" />;
    }

    // Redirect to login if not authenticated
    if (authState.type === 'unauthenticated') {
      const next = `${location.pathname}${location.search}${location.hash}`;
      return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
    }

    if (!roles.includes(authState.user.role)) {
      return (
        <AuthErrorPage
          title="Access Denied"
          message={`Access denied. Your role (${authState.user.role}) lacks required permissions.`}
          icon={<ShieldOff className={styles.accessDeniedIcon} size={64} />}
        />
      );
    }

    // Render children if authenticated
    return <>{children}</>;
  };

  Comp.displayName = `ProtectedRoute(${roles.join(',')})`;

  return Comp;
};

// Create protected routes here, then import them in pageLayout
export const AdminRoute = MakeProtectedRoute(['admin']);
// 'host' has session creation capabilities, 'guest' is read/participate only usually, but often both are considered "authenticated users" for general routes.
// However, the previous "UserRoute" implied general access.
// We will update UserRoute to include all authenticated roles (admin, host, guest) to maintain backward compatibility where UserRoute meant "any logged in user".
// If specific Host-only routes are needed, a new HostRoute should be created by the consumer or here if requested, but for now we map "UserRoute" to all roles.
export const UserRoute = MakeProtectedRoute(['admin', 'host', 'guest']);
export const HostRoute = MakeProtectedRoute(['admin', 'host']);
